import React, { useState } from 'react';
import { Instagram, Facebook, ArrowRight, Check } from 'lucide-react';
import comingSoonImage from '../assets/farzeen-coming-soon-v2.jpg';

const ComingSoonPage = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [joinWhatsapp, setJoinWhatsapp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: firstName,
          email: email,
          phone_number: joinWhatsapp ? phoneNumber : '',
          phone_country_code: '',
          join_whatsapp: joinWhatsapp
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setIsSubmitting(false);
  };

  const [showSplash, setShowSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);

  // Splash screen timing
  React.useEffect(() => {
    // Start fading out splash after 2 seconds
    const fadeTimer = setTimeout(() => {
      setSplashFading(true);
    }, 2000);
    
    // Remove splash completely after fade out (2s + 0.8s fade)
    const removeTimer = setTimeout(() => {
      setShowSplash(false);
    }, 2800);
    
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  // Splash Screen
  if (showSplash) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{ 
          backgroundColor: '#ffffff',
          opacity: splashFading ? 0 : 1,
          transition: 'opacity 0.8s ease-out'
        }}
      >
        <div 
          className="text-center px-8"
          style={{
            animation: 'fadeIn 1.2s ease-in forwards'
          }}
        >
          <p 
            className="text-2xl md:text-3xl lg:text-4xl font-light leading-relaxed tracking-wide"
            style={{ 
              fontFamily: "'Playfair Display', serif", 
              color: '#1a1a1a',
              lineHeight: '1.8'
            }}
          >
            "We don't break.
            <br />
            <span style={{ color: '#8b7355' }}>We break free & build."</span>
          </p>
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col md:flex-row" 
      style={{ 
        backgroundColor: '#ffffff',
        animation: 'fadeIn 0.8s ease-in forwards'
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      
      {/* Left Side - Image */}
      <div className="w-full md:w-1/2 h-[50vh] md:h-screen relative">
        <img 
          src={comingSoonImage} 
          alt="Farzeen" 
          className="w-full h-full object-cover object-top"
          data-testid="coming-soon-image"
        />
      </div>

      {/* Right Side - Content */}
      <div className="w-full md:w-1/2 min-h-[50vh] md:h-screen flex items-center justify-center p-8 md:p-16">
        <div className="max-w-md w-full">
          {/* Top Small Text */}
          <div className="mb-12">
            <p 
              className="text-[10px] tracking-[0.25em] font-light mb-1"
              style={{ color: '#999' }}
            >
              FARZEEN KHAN
            </p>
            <p 
              className="text-[10px] tracking-[0.25em] font-light"
              style={{ color: '#b8a088' }}
            >
              CHAPTER ONE
            </p>
          </div>

          {/* Main Headline */}
          <h1 
            className="text-3xl md:text-4xl font-medium tracking-wide mb-10"
            style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}
            data-testid="coming-soon-heading"
          >
            ON HER TERMS
          </h1>

          {/* Manifesto Copy */}
          <div className="mb-10 space-y-6">
            <p 
              className="text-base font-light leading-relaxed"
              style={{ color: '#555' }}
            >
              She built before.
              <br />
              She built again — <span className="italic">this time on her terms</span>.
            </p>
            <p 
              className="text-base font-light leading-relaxed"
              style={{ color: '#1a1a1a' }}
            >
              We Don't Break.
              <br />
              We <span style={{ color: '#8b7355' }}>Break Free</span> & Build™.
            </p>
          </div>

          {/* Coming Soon - Subtle Timestamp */}
          <p 
            className="text-[10px] tracking-[0.2em] font-light mb-5"
            style={{ color: '#bbb' }}
          >
            COMING SOON
          </p>

          {/* Email Form or Success Message */}
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-0 py-3 text-sm font-light bg-transparent border-b focus:outline-none transition-colors"
                  style={{ borderColor: '#e0e0e0', color: '#1a1a1a' }}
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-0 py-3 text-sm font-light bg-transparent border-b focus:outline-none transition-colors"
                  style={{ borderColor: '#e0e0e0', color: '#1a1a1a' }}
                  data-testid="email-input"
                />
              </div>
              
              {/* WhatsApp Checkbox */}
              <div className="pt-2">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div 
                    className={`w-5 h-5 border flex-shrink-0 flex items-center justify-center transition-all mt-0.5 ${joinWhatsapp ? 'bg-[#25D366] border-[#25D366]' : 'border-gray-300 group-hover:border-gray-400'}`}
                    onClick={() => setJoinWhatsapp(!joinWhatsapp)}
                  >
                    {joinWhatsapp && <Check size={14} style={{ color: '#ffffff' }} />}
                  </div>
                  <span 
                    className="text-sm font-light leading-relaxed"
                    style={{ color: '#666' }}
                    onClick={() => setJoinWhatsapp(!joinWhatsapp)}
                  >
                    Join the BFB WhatsApp Community
                  </span>
                </label>
              </div>

              {/* Phone Number - Shows when WhatsApp is checked */}
              {joinWhatsapp && (
                <div className="animate-fadeIn">
                  <input
                    type="tel"
                    placeholder="Phone Number (with country code)"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-b focus:outline-none transition-colors"
                    style={{ borderColor: '#e0e0e0', color: '#1a1a1a' }}
                    data-testid="phone-input"
                  />
                  <p className="text-xs mt-1" style={{ color: '#999' }}>
                    e.g. +1 647 123 4567
                  </p>
                </div>
              )}
              
              {error && (
                <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-3 py-4 mt-6 text-xs tracking-[0.15em] font-light transition-all hover:opacity-80 disabled:opacity-50"
                style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
                data-testid="notify-button"
              >
                {isSubmitting ? 'JOINING...' : 'JOIN THE FIRST CHAPTER'}
                {!isSubmitting && <ArrowRight size={16} strokeWidth={1.5} />}
              </button>
            </form>
          ) : (
            <div 
              className="p-6 text-center"
              style={{ backgroundColor: '#f8f6f3' }}
              data-testid="success-message"
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: '#8b7355' }}
              >
                <Check size={24} style={{ color: '#ffffff' }} />
              </div>
              <p className="text-base font-light" style={{ color: '#1a1a1a' }}>
                You're on the list!
              </p>
              <p className="text-sm font-light mt-2" style={{ color: '#666' }}>
                We'll notify you when we launch.
              </p>
            </div>
          )}

          {/* Social Links */}
          <div className="mt-12 pt-8" style={{ borderTop: '1px solid #f0f0f0' }}>
            <p className="text-xs font-light mb-4" style={{ color: '#999' }}>
              Follow the journey
            </p>
            <div className="flex items-center gap-6">
              <a 
                href="https://instagram.com/itsfarzeenkhan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-light transition-opacity hover:opacity-60"
                style={{ color: '#1a1a1a' }}
                data-testid="instagram-link"
              >
                <Instagram size={18} strokeWidth={1.5} />
                @itsfarzeenkhan
              </a>
              <a 
                href="https://facebook.com/itsfarzeenkhan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-light transition-opacity hover:opacity-60"
                style={{ color: '#1a1a1a' }}
                data-testid="facebook-link"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;
